import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatCurrency, formatRoas, formatNumber } from "@/lib/formatters";
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from "recharts";

type Metric = "spend" | "roas" | "purchaseValue" | "purchases" | "impressions";
const METRICS: { key: Metric; label: string; color: string }[] = [
  { key: "spend", label: "Spend", color: "#6366F1" },
  { key: "roas", label: "ROAS", color: "#D97706" },
  { key: "purchaseValue", label: "Revenue", color: "#059669" },
  { key: "purchases", label: "Purchases", color: "#B45309" },
  { key: "impressions", label: "Impressions", color: "#5F6D82" },
];

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function SpendRoasTrendChart({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const [activeMetrics, setActiveMetrics] = useState<Metric[]>(["spend", "roas"]);
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard-daily-trend", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("daily-trend", params));
      if (!res.ok) throw new Error("Failed to fetch trend data");
      return res.json();
    },
  });

  const toggle = (m: Metric) => {
    setActiveMetrics(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  };

  const fmt = (key: Metric, v: number) => {
    if (key === "spend" || key === "purchaseValue") return formatCurrency(v);
    if (key === "roas") return formatRoas(v);
    return formatNumber(v);
  };

  const hasLeftAxis = activeMetrics.some(k => k !== "roas");
  const hasRightAxis = activeMetrics.includes("roas");

  const empty = !isLoading && !error && (!data || data.length === 0);

  return (
    <DashboardWidget title="Spend & ROAS Trend" widgetType="chart" isEditMode={isEditMode} isLoading={isLoading} error={error?.message ?? null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#5F6D82" }} data-testid="empty-spend-roas">No trend data for this period</div>
      ) : (
        <div className="px-4 pb-3 h-full flex flex-col" style={{ background: "#F7F9FC" }} data-testid="chart-spend-roas-trend">
          <div className="flex gap-1 mb-2 flex-wrap flex-shrink-0 pt-2">
            {METRICS.map(m => (
              <button
                key={m.key}
                onClick={() => toggle(m.key)}
                className={`px-2 py-0.5 text-[10px] font-medium rounded-full border transition-colors ${
                  activeMetrics.includes(m.key)
                    ? "border-transparent text-white"
                    : "hover:opacity-70"
                }`}
                style={activeMetrics.includes(m.key) ? { backgroundColor: m.color } : { borderColor: "#DCE3ED", color: "#5F6D82" }}
                data-testid={`toggle-${m.key}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-h-0" style={{ minHeight: 200 }}>
            <div className="absolute inset-0">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data || []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#DCE3ED" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#5F6D82" }} tickFormatter={(v: string) => v.slice(5)} />
                  {hasLeftAxis && <YAxis yAxisId="left" tick={{ fontSize: 10, fill: "#5F6D82" }} tickFormatter={(v: number) => formatCurrency(v)} width={60} />}
                  {hasRightAxis && <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10, fill: "#5F6D82" }} tickFormatter={(v: number) => `${v}x`} width={40} />}
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #DCE3ED" }}
                    formatter={(value: number, name: string) => {
                      const m = METRICS.find(x => x.key === name);
                      return [m ? fmt(m.key, value) : value, m?.label || name];
                    }}
                    labelFormatter={(l: string) => l}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={24}
                    iconSize={8}
                    iconType="circle"
                    formatter={(value: string) => {
                      const m = METRICS.find(x => x.key === value);
                      return m?.label || value;
                    }}
                    wrapperStyle={{ fontSize: 10, paddingTop: 4 }}
                  />
                  {activeMetrics.map(key => {
                    const m = METRICS.find(x => x.key === key)!;
                    const yId = key === "roas" ? "right" : "left";
                    return (
                      <Line key={key} yAxisId={yId} type="monotone" dataKey={key} stroke={m.color} strokeWidth={2} dot={false} />
                    );
                  })}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </DashboardWidget>
  );
}
